import { User } from '@velocity-exchange/sdk';
import { DriftWindow } from 'src/window/driftWindow';

const DEFAULT_TIMEOUT_MS = 30_000; // 30 seconds

/**
 * Generates a consistent key for the override map.
 */
function getOverrideKey(userKey: string, marketIndex: number): string {
	return `${userKey}-${marketIndex}`;
}

/**
 * Checks if an isolated position is being liquidated, with dev override support.
 * Returns true if there's an active dev override, otherwise falls back to the real User method.
 */
export function isIsolatedPositionBeingLiquidated(
	user: User | undefined,
	marketIndex: number,
	userKey: string
): boolean {
	if (!user) return false;

	// Check for dev override first
	const key = getOverrideKey(userKey, marketIndex);
	const overrideExpiration =
		DriftWindow.devIsolatedLiquidationOverrides.get(key);
	if (overrideExpiration !== undefined && overrideExpiration > Date.now()) {
		return true;
	}

	// Fall back to real User method
	return user.isIsolatedPositionBeingLiquidated(marketIndex);
}

/**
 * Gets health for a position, with dev override support for isolated positions.
 * Returns 0 if there's an active dev override for an isolated position, otherwise falls back to the real User method.
 */
export function getHealthWithOverride(
	user: User | undefined,
	marketIndex: number | undefined,
	userKey: string | undefined
): number {
	if (!user) return 100;

	// For isolated positions, check for dev override
	if (marketIndex !== undefined && userKey !== undefined) {
		const key = getOverrideKey(userKey, marketIndex);
		const overrideExpiration =
			DriftWindow.devIsolatedLiquidationOverrides.get(key);
		if (overrideExpiration !== undefined && overrideExpiration > Date.now()) {
			return 0;
		}
	}

	// Fall back to real User method
	return user.getHealth(marketIndex);
}

/**
 * Sets a dev override to mark an isolated position as liquidated.
 * @param userKey - The user key of the subaccount
 * @param marketIndex - The market index of the isolated position
 * @param timeoutMs - Duration in milliseconds (default: 30 seconds)
 */
export function setDevLiquidationOverride(
	userKey: string,
	marketIndex: number,
	timeoutMs: number = DEFAULT_TIMEOUT_MS
): void {
	const key = getOverrideKey(userKey, marketIndex);
	const expirationTimestamp = Date.now() + timeoutMs;
	DriftWindow.devIsolatedLiquidationOverrides.set(key, expirationTimestamp);
}

/**
 * Clears a dev override for an isolated position.
 */
export function clearDevLiquidationOverride(
	userKey: string,
	marketIndex: number
): void {
	const key = getOverrideKey(userKey, marketIndex);
	DriftWindow.devIsolatedLiquidationOverrides.delete(key);
}

/**
 * Checks if a dev override is currently active for a user and market index.
 */
export function hasDevLiquidationOverride(
	userKey: string,
	marketIndex: number
): boolean {
	const key = getOverrideKey(userKey, marketIndex);
	const expiration = DriftWindow.devIsolatedLiquidationOverrides.get(key);
	return expiration !== undefined && expiration > Date.now();
}

/**
 * Cleans up expired overrides from the map.
 * Returns true if any overrides were removed.
 */
export function cleanupExpiredOverrides(): boolean {
	const now = Date.now();
	let removed = false;

	for (const [key, expiration] of DriftWindow.devIsolatedLiquidationOverrides) {
		if (expiration <= now) {
			DriftWindow.devIsolatedLiquidationOverrides.delete(key);
			removed = true;
		}
	}

	return removed;
}
